"use client";

import { useCallback, useRef } from "react";
import { track } from "../../core/client";

/**
 * 탭 클릭 추적 훅 (사용자용)
 * 탭 인덱스, 체류시간, repeat_click 등을 자동으로 관리
 *
 * @param eventName - 'main_tab_click' | 'stock_tab_click' | 'strategy_tab_click'
 * @param tabKeys - 탭 키 배열 (순서대로)
 * @param initialTab - 초기 활성 탭 (optional)
 */
export function useTabTracking(
    eventName: "main_tab_click" | "stock_tab_click" | "strategy_tab_click",
    tabKeys: string[],
    initialTab?: string
) {
    const currentTabRef = useRef<string>(initialTab || tabKeys[0]);
    const tabEnterTimeRef = useRef<number>(Date.now());

    // 탭 클릭 추적
    const trackClick = useCallback(
        (
            toTab: string,
            trigger: "tab" | "shortcut" = "tab",
            sourceKey?: string
        ) => {
            const fromTab = currentTabRef.current;
            const toTabIndex = tabKeys.indexOf(toTab);
            const fromTabIndex = tabKeys.indexOf(fromTab);

            // 유효하지 않은 탭 키
            if (toTabIndex === -1) {
                console.warn(
                    `[useTabTracking] Invalid tab key: ${toTab}. Available: ${tabKeys.join(
                        ", "
                    )}`
                );
                return;
            }

            // 체류시간 계산
            const dwellPrevMs = Date.now() - tabEnterTimeRef.current;

            // repeat_click 체크
            const repeatClick = toTab === fromTab;

            track(eventName, {
                to_tab: toTab,
                to_tab_index: toTabIndex,
                from_tab: fromTab,
                from_tab_index: fromTabIndex >= 0 ? fromTabIndex : 0,
                dwell_prev_ms: dwellPrevMs,
                repeat_click: repeatClick,
                trigger,
                source_key: sourceKey || undefined,
            });

            // 상태 업데이트 (repeat_click이 아닐 때만)
            if (!repeatClick) {
                currentTabRef.current = toTab;
                tabEnterTimeRef.current = Date.now();
            }
        },
        [eventName, tabKeys]
    );

    // 프로그래밍 방식으로 탭 변경 (이벤트 발생 없이)
    const setCurrentTab = useCallback(
        (tab: string) => {
            if (tabKeys.indexOf(tab) === -1) {
                console.warn(`[useTabTracking] Invalid tab key: ${tab}`);
                return;
            }
            currentTabRef.current = tab;
            tabEnterTimeRef.current = Date.now();
        },
        [tabKeys]
    );

    return {
        trackClick,
        setCurrentTab,
        getCurrentTab: () => currentTabRef.current,
    };
}

/**
 * 마켓 탭 클릭 추적 훅 (사용자용)
 * 국내/해외/크립토 마켓 전환 추적
 *
 * @param initialMarket - 초기 마켓 (optional, 기본값: 'kr')
 */
export function useMarketTabTracking(
    initialMarket: "kr" | "us" | "crypto" = "kr"
) {
    const currentMarketRef = useRef<"kr" | "us" | "crypto">(initialMarket);
    const marketEnterTimeRef = useRef<number>(Date.now());

    // 마켓 탭 클릭 추적
    const trackClick = useCallback((market: "kr" | "us" | "crypto") => {
        // 체류시간 계산
        const dwellPrevMs = Date.now() - marketEnterTimeRef.current;

        track("market_tab_click", {
            market,
            dwell_prev_ms: dwellPrevMs,
        });

        // 상태 업데이트
        currentMarketRef.current = market;
        marketEnterTimeRef.current = Date.now();
    }, []);

    // 프로그래밍 방식으로 마켓 변경 (이벤트 발생 없이)
    const setCurrentMarket = useCallback((market: "kr" | "us" | "crypto") => {
        currentMarketRef.current = market;
        marketEnterTimeRef.current = Date.now();
    }, []);

    return {
        trackClick,
        setCurrentMarket,
        getCurrentMarket: () => currentMarketRef.current,
    };
}

